import {
  Button,
  ButtonProps,
  Menu,
  MenuDivider,
  MenuGroup,
  MenuGroupHeader,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  PositioningShorthand
} from "@fluentui/react-components";
import { MoreHorizontal20Regular, ChevronDown16Regular } from "@fluentui/react-icons";
import { Fragment, JSX, useState } from "react";

import { renderIcon } from "libs/render-icon";

import "./ContextualMenuV2.scss";

export type MenuGroups = {
  title: string;
  className?: string;
  groupItems: ListItem[];
};

type Props = {
  menuItems?: ListItem[];
  menuGroups?: MenuGroups[];
  buttonText?: string;
  buttonStyle?: string;
  buttonAppearance?: ButtonProps["appearance"];
  positioning?: PositioningShorthand;
  closeOnScroll?: boolean;
  buttonIcon?: () => JSX.Element;
  buttonDisabled?: boolean;
  showChevron?: boolean;
};

export type SubMenu = {
  submenuItems: ListItem[];
  buttonText?: string;
  buttonStyle?: string;
  buttonAppearance?: ButtonProps["appearance"];
  buttonIcon?: () => JSX.Element;
  buttonDisabled?: boolean;
};

export type ListItem = {
  hasDivider?: boolean;
  text?: string;
  icon?: () => JSX.Element;
  disabled?: boolean;
  onClick?: (e: React.MouseEvent<HTMLElement>) => void;
  className?: string;
  content?: JSX.Element;
  persistOnClick?: boolean;
  hidden?: boolean;
  activeClassName?: string;
  key?: string;
  submenu?: SubMenu;
  name?: string;
  value?: string;
};

function ContextualMenu({
  menuItems,
  menuGroups,
  buttonText = "",
  buttonStyle,
  buttonAppearance = "transparent",
  buttonIcon = () =>
    renderIcon(<MoreHorizontal20Regular className="fw-bold cursor-pointer" />),
  positioning,
  closeOnScroll = false,
  buttonDisabled = false,
  showChevron = false
}: Props) {
  const [isOpen, setIsOpen] = useState(false);

  const defaultButtonStyle =
    "ghost-button min-width-fit-content justify-content-start";

  const triggerButton = (
    style: string,
    appearance: ButtonProps["appearance"],
    icon?: () => JSX.Element,
    text?: string,
    disabled?: boolean
  ) => (
    <Button
      className={`${style} min-w-0`}
      appearance={appearance}
      disabled={disabled}
      onClick={(e) => e.stopPropagation()}
    >
      {icon && icon()}
      <span>{text}</span>
      {showChevron && (
        <ChevronDown16Regular
          className={`contextual-menu-chevron ${isOpen ? "open" : ""}`}
        />
      )}
    </Button>
  );

  const menuItem = (item: ListItem) => (
    <MenuItem
      onClick={item.onClick}
      disabled={item.disabled}
      icon={item.icon && item.icon()}
      persistOnClick={item.persistOnClick}
      hidden={item.hidden}
      className={`${item.activeClassName ?? ""} p-0`}
    >
      <div className="d-flex align-items-center">
        {item.content}
        {item.text && <div className={item.className}>{item.text}</div>}
        {item.submenu && (
          <MenuTrigger>
            {triggerButton(
              item.submenu.buttonStyle ?? defaultButtonStyle,
              item.submenu.buttonAppearance ?? "transparent",
              item.submenu.buttonIcon,
              item.submenu.buttonText,
              item.submenu.buttonDisabled
            )}
          </MenuTrigger>
        )}
      </div>
    </MenuItem>
  );

  const getMenuItem = (item: ListItem) => {
    if (item.hidden) {
      return null;
    }

    if (item.submenu) {
      return (
        <Menu>
          {menuItem(item)}
          <MenuPopover>
            <MenuList>
              {item.submenu.submenuItems.map((subItem) =>
                renderMenuItem(subItem)
              )}
            </MenuList>
          </MenuPopover>
        </Menu>
      );
    }

    return menuItem(item);
  };

  const renderMenuItem = (item: ListItem) => (
    <Fragment key={item.key ?? item.text}>
      {getMenuItem(item)}
      {item.hasDivider && <MenuDivider key={`divider-${item.text}`} />}
    </Fragment>
  );

  return (
    <Menu
      closeOnScroll={closeOnScroll}
      positioning={positioning}
      onOpenChange={(e, data) => setIsOpen(data.open)}
    >
      <MenuTrigger disableButtonEnhancement>
        {triggerButton(
          buttonStyle ?? defaultButtonStyle,
          buttonAppearance,
          buttonIcon,
          buttonText,
          buttonDisabled
        )}
      </MenuTrigger>
      <MenuPopover>
        <MenuList>
          {menuItems && menuItems.map((item) => renderMenuItem(item))}
          {menuGroups &&
            menuGroups.map((group, index) => (
              <Fragment key={`section-${group.title}`}>
                <MenuGroup>
                  <MenuGroupHeader className={group.className}>
                    {group.title}
                  </MenuGroupHeader>
                  {group.groupItems.map((item) => renderMenuItem(item))}
                </MenuGroup>
                {index !== menuGroups.length - 1 && <MenuDivider />}
              </Fragment>
            ))}
        </MenuList>
      </MenuPopover>
    </Menu>
  );
}

export default ContextualMenu;
